import { getAccessorType, getterTree, actionTree, mutationTree } from "typed-vuex";
import { Response } from "@wl/types/lib/responses";
import { InfoRes } from "@wl/types/lib/responses/info.res";
import { actionModule } from "./action";
import { rankingModule } from "./ranking";
import { tabModule } from "./tab";
import { userModule } from "./user";
import { adminModule } from "./admin";
import { NavigationTab } from "~/types/types";

type Info = Response<InfoRes>;

/**
 * Root state.
 *
 * @export
 * @interface RootState
 */
export interface RootState {
    info: InfoRes | null;
}

/**
 * Initial root state.
 *
 * @return {(() => RootState)}
 */
export const state = (): RootState => ({
    info: null,
});

/**
 * Root module getters.
 */
export const getters = getterTree(state, {});

/**
 * Root module mutations.
 */
export const mutations = mutationTree(state, {
    /**
     * Sets info into the state.
     *
     * @param {RootState} state Root module state
     * @param {InfoRes} info Info to set
     */
    setInfo(state: RootState, info: InfoRes | undefined): void {
        if (!info) return;

        state.info = info;
    },
});

/**
 * Root module actions.
 */
export const actions = actionTree(
    { state, getters, mutations },
    {
        /**
         * Fetches the server info.
         *
         * @return {Promise<void>}
         */
        async getInfo(): Promise<void> {
            const response = await this.$axios.get<Info>("/info");
            if (response.data.error) return;

            this.app.$accessor.setInfo(response.data.data);
        },

        /**
         * Fetches data from all state modules.
         *
         * @return {Promise<void>}
         */
        async fetch(): Promise<void> {
            await this.app.$accessor.getInfo();
            if (this.app.$accessor.info?.underMaintenance) return;

            if (this.app.$accessor.user.isLoggedIn) {
                const error = await this.app.$accessor.user.fetch();
                if (!error) {
                    await this.app.$accessor.action.fetch();
                    await this.app.$accessor.ranking.fetch();
                }

                if (this.app.$accessor.tab.active === NavigationTab.Dashboard) {
                    if (!this.app.$accessor.user.isAdmin) {
                        await this.app.$accessor.tab.changeTab(NavigationTab.Quests);
                    } else {
                        await this.app.$accessor.admin.fetch();
                    }
                }
            }
        },

        /**
         * Clears the root module and all sub
         */
        clear(): void {
            this.app.$accessor.tab.clear();
            this.app.$accessor.user.clear();
            this.app.$accessor.action.clear();
            this.app.$accessor.ranking.clear();
        },
    },
);

export const accessorType = getAccessorType({
    state,
    getters,
    mutations,
    actions,
    modules: {
        user: userModule,
        tab: tabModule,
        action: actionModule,
        ranking: rankingModule,
        admin: adminModule,
    },
});
